import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Box, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';

import { PresentationChart01Icon } from '@/components/icons/PresentationChart01Icon';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { COLORS } from '@/styles/tokens/colors';

import { usePresentationTemplateEditorContext } from '../../PresentationTemplateEditor.context';
import { DraggableBundleTile } from './bundleTile/DraggableBundleTile';

export function PresentationTemplateStageEditorMain() {
  const {
    bundlesFormArray: { fields: bundles, move },
  } = usePresentationTemplateEditorContext();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require pointer to move 8px before activating drag, allowing clicks to pass through
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (isEmpty(bundles)) return <EmptyBundlesState />;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    const oldIndex = bundles.findIndex((bundle) => bundle.id === active.id);
    const newIndex = bundles.findIndex((bundle) => bundle.id === over.id);
    move(oldIndex, newIndex);
  };

  return (
    <Stack width="100%" spacing={4} p={3} overflow="auto">
      <Typography variant="title-s">
        Drag the page thumbnails below to reorder.
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={bundles.map((bundle) => bundle.id)}
          strategy={rectSortingStrategy}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              rowGap: 3,
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              alignContent: 'flex-start',
              justifyContent: 'flex-start',
            }}
          >
            {bundles.map(({ id: bundleId }, index) => (
              <DraggableBundleTile
                key={bundleId}
                index={index}
                bundleId={bundleId}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Stack>
  );
}

function EmptyBundlesState() {
  return (
    <Stack
      width="100%"
      height="100%"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <ActionCard
        variant="noCard"
        icon={<PresentationChart01Icon size={32} color={COLORS.TEXT.PRIMARY} />}
        heading="No pages"
        description="You haven't added any pages to your template yet. Click on a slide from the left to add it to your template."
        sx={{ maxWidth: 400 }}
      />
    </Stack>
  );
}
